import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "motion/react";
import { OrbitalRings } from "./ui/OrbitalRings";
import { ImageWithFallback } from "./ImageWithFallback";
import mascot from "../assets/geekcow-mascot.png";

const TESTIMONIALS = [
  {
    quote:
      "Olga helped me establish my business in the digital world in a clear and efficient way. She guided me in making the most of my initial investment, avoiding unnecessary expenses. I look forward to continuing our collaboration to ensure my business thrives and grows.",
    name: "Michelle Guerra",
    role: "",
    company: "GlowTogether",
    accent: "#22c365",
    initials: "MG",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed884fa052b3080df6fe5_Miche%20Guerra.avif",
    linkedin: "https://www.linkedin.com/in/miche-guerra/",
  },
  {
    quote:
      "Olga helped our foundation amazingly with the design of new certificates and wireframes for the website. I truly admire both her no-nonsense mindset and proactive way of working. She has positive energy and loves the work she does!",
    name: "Giel Boes",
    role: "",
    company: "BoesBos",
    accent: "#256af4",
    initials: "GB",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed6279747f9c49d1d0fd2_805a3d814342fe760fa8455b481a792b_Giel%20Boes.jpg",
    linkedin: "https://www.linkedin.com/in/gielboes/",
  },
  {
    quote:
      "She helped us with great ideas and with nice visuals to accompany our social media posts. Work with Olga went really nice in terms of communication, proactiveness and creativity. We especially liked the energy, enthusiasm and openness she brought to our team.",
    name: "Robert Monné",
    role: "",
    company: "Analytics for a Better World",
    accent: "#f42536",
    initials: "RM",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed62693236fd774535029_ad646cb9b8ec8dd1e6e3ff2eb02eca7f_Robert%20Monne.jpg",
    linkedin: "https://www.linkedin.com/in/robertmonne/",
  },
  {
    quote:
      "Olga is a professional and detail-oriented designer who pays attention to the customer needs and is open to adapt so as to satisfy all the given requirements. Besides her professional capabilities, she is a joy to work with, honest, happy, and willing to keep learning and growing.",
    name: "Claudia Orellana-Rodriguez",
    role: "",
    company: "University of Amsterdam",
    accent: "#01E12D",
    initials: "CO",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed626f36dcf23e42c64bd_6f974cd75c7780de7cbf536d5ed4ceec_Claudia%20Orellana-Rodriguez.jpg",
    linkedin: "https://www.linkedin.com/in/claudia-orellana-rodriguez/",
  },
  {
    quote:
      "Olga is a fast learner and talented professional, passionate about designing the best product for her users and always willing to take up the next challenge. I love her energy, openness and enthusiasm which makes her a great colleague to have in your team.",
    name: "Angela Fusaro",
    role: "",
    company: "Every Mother Knows",
    accent: "#256af4",
    initials: "AF",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed62694cf6f09d8be81a9_daed740555fc73b0055be76312499462_Angela%20Fusaro.jpg",
    linkedin: "https://www.linkedin.com/in/angelafusaro/",
  },
  {
    quote:
      "She's a great leader, enthusiastic about technology and passionate about illustration. We worked in a team, and I learned from her organization capacity and her great personality.",
    name: "María Inés Peñate",
    role: "",
    company: "Publigrafik Digital",
    accent: "#f42536",
    initials: "MI",
    photo: "https://cdn.prod.website-files.com/63f66c95045c0480956be317/683ed626c2dcec97f7b48c23_09996075c8ad3b3e90d9eb588893aa2e_Maria%20Ines%20penate.jpg",
    linkedin: "https://www.linkedin.com/in/in%C3%A9s-pe%C3%B1ate-56307013b/",
  },
  {
    quote:
      "Olga is one of the most remarkable women I have ever met. Her capacity of abstraction of an idea is out of this world. In the time we worked together she went beyond any expectations, her work ethic and creativity are outstanding. I consider Olga a friend and an example of getting things done without hesitation.",
    name: "Ricardo Roesch",
    role: "Security Transformation Manager",
    company: "",
    accent: "#22c365",
    initials: "RR",
    photo: "https://media.licdn.com/dms/image/v2/D4E03AQFP_MotrVFH3g/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1694956849972?e=2147483647&v=beta&t=1nWGiDY6TRq4MdVDu8P3hSPCHZNQIGYaKFNV65c3uW0",
    linkedin: "https://www.linkedin.com/in/ricardoroesch/",
  },
  {
    quote:
      "Olga is an excellent creative, passionate and dedicated, she finds solutions out of the ordinary and always gives her best.",
    name: "Vivian Valdés",
    role: "Photography & Graphic Design",
    company: "",
    accent: "#256af4",
    initials: "VV",
    photo: "",
    linkedin: "https://www.linkedin.com/in/vivian-vald%C3%A9s-72b91647/",
  },
];

type Testimonial = (typeof TESTIMONIALS)[number];

/**
 * One card, matching build/TestimonialSection.svg: square corners, #F8F8FB fill,
 * quote on top and the author pinned to the bottom. Cards are bottom-aligned in
 * the strip and grow upward, so their heights vary with the length of the quote —
 * that ragged top edge is the design's, not an accident.
 */
function TestimonialCard({ t, ariaHidden }: { t: Testimonial; ariaHidden?: boolean }) {
  const [imgError, setImgError] = useState(false);

  const author = (
    <div className="flex items-center gap-4">
      <span
        className="h-[52px] w-[52px] shrink-0 overflow-hidden rounded-full"
        style={{ backgroundColor: t.accent + "22" }}
      >
        {t.photo && !imgError ? (
          <img
            src={t.photo}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center">
            <span
              className="text-[15px] font-bold leading-none"
              style={{ color: t.accent === "#01E12D" ? "#007a1a" : t.accent }}
            >
              {t.initials}
            </span>
          </span>
        )}
      </span>

      <span className="flex flex-col gap-1">
        <span className="text-[17px] font-medium leading-tight text-[#151618]">{t.name}</span>
        <span className="text-[13px] leading-tight text-[#676a70]">
          {t.role && t.company ? `${t.role}, ${t.company}` : t.role || t.company}
        </span>
      </span>
    </div>
  );

  return (
    <li
      className="flex w-[300px] shrink-0 flex-col justify-end gap-8 bg-[#f8f8fb] p-7 md:w-[420px] md:p-9"
      aria-hidden={ariaHidden || undefined}
    >
      <p className="text-[15px] leading-[1.5] text-[#151618] md:text-[17px]">{t.quote}</p>
      {t.linkedin && !ariaHidden ? (
        <a
          href={t.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View ${t.name} on LinkedIn`}
          className="transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b]"
        >
          {author}
        </a>
      ) : (
        author
      )}
    </li>
  );
}

/** Pixels per second: cruising, and while the visitor is reading it. */
const SCROLL_SPEED = 42;
const SCROLL_SPEED_SLOW = 12;

/**
 * Drives the carousel by moving `scrollLeft` rather than translating a track.
 *
 * That choice is what makes manual control possible: the viewport is a real
 * scroller, so trackpad, touch swipe and the drag handler below all work, and
 * the auto-advance simply adds to the same value. A CSS transform marquee
 * cannot be grabbed.
 *
 * It slows rather than stops on hover — the visitor is probably reading, but a
 * dead carousel looks broken.
 */
function useAutoScroll() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const slowRef = useRef(false);
  const pausedRef = useRef(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let last = performance.now();
    // The authoritative position is kept here as a float and assigned absolutely.
    // Doing `scrollLeft += speed * dt` instead loses everything below a pixel:
    // Chrome snaps each write, so 0.2px/frame rounds to zero and the carousel
    // sits perfectly still, while 0.7px/frame rounds up to a full 1px and runs
    // ~40% too fast. Accumulating first and assigning the total fixes both.
    let pos = viewport.scrollLeft;
    let lastWritten = pos;

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      // If the position moved without us writing it, the visitor scrolled or
      // dragged — adopt their position rather than fighting them.
      if (Math.abs(viewport.scrollLeft - lastWritten) > 2) pos = viewport.scrollLeft;

      if (!pausedRef.current) {
        pos += (slowRef.current ? SCROLL_SPEED_SLOW : SCROLL_SPEED) * dt;
      }

      // The list is rendered twice, so rewinding by half is invisible.
      const half = track.scrollWidth / 2;
      if (half > 0) {
        if (pos >= half) pos -= half;
        else if (pos < 0) pos += half;
      }

      viewport.scrollLeft = pos;
      lastWritten = viewport.scrollLeft;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const slow = () => (slowRef.current = true);
    const resume = () => (slowRef.current = false);
    viewport.addEventListener("mouseenter", slow);
    viewport.addEventListener("mouseleave", resume);
    viewport.addEventListener("focusin", slow);
    viewport.addEventListener("focusout", resume);

    // Drag to scroll. Pointer capture keeps it tracking outside the element.
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      dragging = true;
      moved = 0;
      pausedRef.current = true;
      startX = e.clientX;
      startScroll = viewport.scrollLeft;
      viewport.setPointerCapture(e.pointerId);
      viewport.style.cursor = "grabbing";
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      viewport.scrollLeft = startScroll - dx;
      // Keep the infinite loop working mid-drag, in both directions.
      const half = track.scrollWidth / 2;
      if (viewport.scrollLeft >= half) {
        viewport.scrollLeft -= half;
        startScroll -= half;
      } else if (viewport.scrollLeft <= 0) {
        viewport.scrollLeft += half;
        startScroll += half;
      }
    };
    const endDrag = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      pausedRef.current = false;
      if (viewport.hasPointerCapture(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
      viewport.style.cursor = "grab";
    };
    // Swallow the click that ends a drag, so dragging never opens a profile.
    const onClick = (e: MouseEvent) => {
      if (moved > 6) {
        e.preventDefault();
        e.stopPropagation();
        moved = 0;
      }
    };

    viewport.addEventListener("pointerdown", onPointerDown);
    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", endDrag);
    viewport.addEventListener("pointercancel", endDrag);
    viewport.addEventListener("click", onClick, true);

    return () => {
      cancelAnimationFrame(raf);
      viewport.removeEventListener("mouseenter", slow);
      viewport.removeEventListener("mouseleave", resume);
      viewport.removeEventListener("focusin", slow);
      viewport.removeEventListener("focusout", resume);
      viewport.removeEventListener("pointerdown", onPointerDown);
      viewport.removeEventListener("pointermove", onPointerMove);
      viewport.removeEventListener("pointerup", endDrag);
      viewport.removeEventListener("pointercancel", endDrag);
      viewport.removeEventListener("click", onClick, true);
    };
  }, []);

  return { viewportRef, trackRef };
}

interface TestimonialsSectionProps {
  /** Voice differs per face — the agency speaks as "us", the profile as "I". */
  eyebrow?: string;
  heading?: React.ReactNode;
  subtitle?: string;
}

const DEFAULT_HEADING = (
  <>
    <span className="text-[#141414]">Real humans, </span>
    <span className="text-[#f42536]">real</span>
    <span className="text-[#141414]"> projects</span>
  </>
);

export function TestimonialsSection({
  eyebrow,
  heading = DEFAULT_HEADING,
  subtitle,
}: TestimonialsSectionProps = {}) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Same scroll-driven rotation the heroes use, so every dashed ring field on
  // the site behaves identically instead of this one spinning on its own.
  const ringRotations = [
    useTransform(scrollYProgress, [0, 1], [0, 300]),
    useTransform(scrollYProgress, [0, 1], [0, -220]),
    useTransform(scrollYProgress, [0, 1], [0, 150]),
    useTransform(scrollYProgress, [0, 1], [0, -80]),
  ];

  const { viewportRef, trackRef } = useAutoScroll();

  // The track holds the list twice so the loop can reset invisibly at halfway.
  // The second copy is aria-hidden: duplicated quotes would otherwise be read out.
  const loop = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section ref={ref} className="gc-ring-field relative w-full overflow-hidden py-16 md:py-24">
      <OrbitalRings variant="flat" scrollRotations={ringRotations} className="opacity-70" />

      <div className="relative z-10 flex flex-col gap-10 md:gap-14">
        <div className="flex flex-col items-center gap-4 px-4 text-center md:px-10 lg:px-16">
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4 }}
              className="font-mono text-[12px] uppercase tracking-[3px] text-[#666] md:text-[14px]"
            >
              {eyebrow}
            </motion.p>
          )}

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-black leading-[1.02] tracking-[-2px] text-[42px] md:text-[76px] lg:text-[96px]"
          >
            {heading}
          </motion.h2>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="max-w-[600px] text-[16px] leading-[28px] text-[#666] md:text-[20px]"
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        {/* Carousel. items-end is what makes the cards share a bottom edge and
            grow upward, exactly as the rects are laid out in the design. */}
        <div
          ref={viewportRef}
          className="gc-carousel w-full overflow-x-auto overscroll-x-contain"
          style={{ cursor: "grab" }}
          role="region"
          aria-label="Client testimonials"
        >
          <ul ref={trackRef} className="flex w-max items-end gap-4">
            {loop.map((t, i) => (
              <TestimonialCard key={`${t.name}-${i}`} t={t} ariaHidden={i >= TESTIMONIALS.length} />
            ))}
          </ul>
        </div>
      </div>

      {/* Mascot overlaps the cards, as in the design: its rect starts 112px above
          the cards' shared bottom edge, and it sits at -10°. */}
      <div className="pointer-events-none relative z-20 -mt-[76px] flex justify-center md:-mt-[112px] md:justify-start md:pl-[52%]">
        <ImageWithFallback
          src={mascot}
          alt=""
          aria-hidden="true"
          className="h-auto w-[160px] -rotate-[10deg] drop-shadow-2xl md:w-[220px]"
        />
      </div>

      <p className="sr-only">
        {TESTIMONIALS.length} client testimonials, scrolling automatically. Hover or focus to pause.
      </p>
    </section>
  );
}
